import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Input } from "@/components/erp/ui";
import { toast } from "sonner";

const BREAK = ["LUNCH", "DINNER", "PRAY", "TOILET"];
const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const iso = (d) => d.toLocaleDateString("en-CA");
const monday = (off) => {
  const t = new Date(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }) + "T00:00:00");
  t.setDate(t.getDate() - ((t.getDay() + 6) % 7) + off * 7);
  return t;
};
const h = (s) => Math.round((s || 0) / 36) / 100;

const DesignerChart = ({ name, data }) => (
  <Card className="p-4" data-testid={`weekly-chart-${name}`}>
    <div className="text-sm font-semibold mb-2">{name}</div>
    <div className="h-52">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 0, right: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={36} tickFormatter={(t) => `${Math.round(t * 10) / 10}j`} />
          <Tooltip formatter={(v) => `${v} jam`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="project" name="Jam Project" stackId="a" fill="#10b981" />
          <Bar dataKey="istirahat" name="Istirahat" stackId="a" fill="#f97316" />
          <Bar dataKey="pause" name="Pause" stackId="a" fill="#94a3b8" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Card>
);

export const DesignWeeklyChart = ({ email = "" }) => {
  const [off, setOff] = useState(0);
  const [rows, setRows] = useState([]);
  const start = monday(off);
  const end = new Date(start); end.setDate(end.getDate() + 6);
  const s = iso(start), e = iso(end);
  useEffect(() => { api.get("/design/recap", { params: { start: s, end: e, email } }).then(({ data }) => setRows(data.rows || [])).catch(() => setRows([])); }, [s, e, email]);
  const byUser = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const u = (m[r.user_name || r.user_email] ||= DAYS.map((day, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return { day, date: iso(d), project: 0, istirahat: 0, pause: 0 }; }));
      const x = u.find((d) => d.date === r.date);
      if (!x) return;
      x.project = h(r.work_seconds);
      x.istirahat = h(BREAK.reduce((t, k) => t + (r.by_status[k] || 0), 0));
      x.pause = h(r.by_status.PAUSE);
    });
    return m;
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3" data-testid="design-weekly">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">Grafik Mingguan: Jam Project vs Istirahat</span>
        <div className="ml-auto flex items-center gap-1 text-xs">
          <button type="button" data-testid="weekly-prev" onClick={() => setOff(off - 1)} className="p-1 rounded border border-slate-300"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-mono px-1" data-testid="weekly-range">{s} – {e}</span>
          <button type="button" data-testid="weekly-next" disabled={off >= 0} onClick={() => setOff(off + 1)} className="p-1 rounded border border-slate-300 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(byUser).map(([n, d]) => <DesignerChart key={n} name={n} data={d} />)}
        {Object.keys(byUser).length === 0 && <p className="text-sm text-slate-400">Belum ada data kerja di minggu ini.</p>}
      </div>
    </div>
  );
};

export const WorkEndSetting = ({ manager }) => {
  const [v, setV] = useState("");
  const [saved, setSaved] = useState("");
  useEffect(() => { api.get("/design/settings").then(({ data }) => { setV(data.work_end); setSaved(data.work_end); }).catch(() => {}); }, []);
  const save = async () => {
    if (!v || v === saved) return;
    try { await api.put("/design/settings", { work_end: v }); setSaved(v); toast.success(`Jam kerja selesai: ${v}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan jam kerja"); setV(saved); }
  };
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="design-work-end">
      Jam kerja selesai:
      {manager ? <Input type="time" data-testid="design-work-end-input" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} className="border border-slate-300 rounded px-1.5 py-1" /> : <b>{v}</b>}
      <span className="text-slate-400">· pengingat Lupa Stop dikirim setelah jam ini (kecuali Lembur)</span>
    </span>
  );
};
